import { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  DMAutomationPlatform,
  FacebookPage,
  PlanTypeV4,
} from "@markit/common.types";
import { hasSubscription } from "@markit/common.utils";
import IntegrationPanelContainer from "../../CreatorProfile/Integration/IntegrationPanelContainer";
import { HorizontalDivider } from "../../../Dividers/HorizontalDivider";
import RectangleButton from "../../../Buttons/RectangleButton";
import { Colors } from "../../../../utils/colors";
import { getAccountSlice } from "../../../../redux/slices/accountSlice";
import { notificationActions } from "../../../../redux/slices/notificationSlice";
import { facebookPageAsProfilePicAccount } from "../../../../utils/facebookApiUtils";
import { getAccountConnectionStatusDisplay } from "../../../../utils/socialAccountUtils";
import ManageFacebookAccountModal from "./ManageFacebookAccountModal";
import ConnectFacebookModal from "./ConnectFacebookModal";
import SocialProfilePic from "../../../DisplayItem/AutoDM/SocialProfilePic";
import { SocialPlatformIconItem } from "../../../DisplayItem/AutoDM/SocialPlatformIconItem";

// Matches MAX_CONNECTED_FACEBOOK_PAGES in the backend's FacebookApiController.ts -
// keep both in sync if this changes.
export const MAX_FACEBOOK_PAGES = 5;

export const FacebookPanelSection = () => {
  const { accountData, facebookPages } = useSelector(getAccountSlice);
  const dispatch = useDispatch();
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [manageAccountId, setManageAccountId] = useState("");

  const hasSub = useMemo(
    () => hasSubscription(accountData) || accountData.isAdmin,
    [accountData]
  );

  const atCap = useMemo(
    () => facebookPages.length >= MAX_FACEBOOK_PAGES,
    [facebookPages.length]
  );

  const facebookPage = useMemo(
    () => facebookPages.find((page) => page.id === manageAccountId),
    [facebookPages, manageAccountId]
  );

  const connectOnPress = useCallback(() => {
    if (hasSub) {
      setShowConnectModal(true);
    } else {
      dispatch(
        notificationActions.setMarkitPlusModal(PlanTypeV4.ESSENTIAL.name)
      );
    }
  }, [dispatch, hasSub]);

  const renderAccountRow = useCallback(
    (facebookPage: FacebookPage, isLast: boolean) => {
      const statusDisplay = getAccountConnectionStatusDisplay(
        facebookPage.status
      );
      return (
        <div key={facebookPage.id}>
          <div
            className="AlignedRowSpaced"
            style={{ paddingInline: 14, gap: 14 }}
          >
            <div className="AlignedRow" style={{ gap: 12, minWidth: 0 }}>
              <SocialProfilePic
                account={facebookPageAsProfilePicAccount(facebookPage)}
                size={40}
              />
              <div className="ColumnNormal" style={{ gap: 4, minWidth: 0 }}>
                <div className="AlignedRow" style={{ gap: 4 }}>
                  <SocialPlatformIconItem
                    platform={DMAutomationPlatform.FACEBOOK}
                    size={16}
                    noBorder
                  />
                  <span className="bodyRegular">{facebookPage.pageName}</span>
                </div>
                <div className="AlignedRow" style={{ gap: 6 }}>
                  <div
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: 3,
                      backgroundColor: statusDisplay.color,
                    }}
                  />
                  <span className="smallBodySubtext">
                    {statusDisplay.label}
                  </span>
                </div>
              </div>
            </div>
            <RectangleButton
              buttonLabel="Manage"
              onPress={() => setManageAccountId(facebookPage.id)}
              altColor={Colors.WHITE1}
              altTextColor={Colors.BLACK}
              altBorderRadius={100}
              altPaddingHorz={14}
              altPaddingVert={10}
              containerStyles={{
                border: `0.5px solid ${Colors.GRAY2}`,
                width: "fit-content",
              }}
            />
          </div>
          {!isLast ? <HorizontalDivider altMargin={14} /> : null}
        </div>
      );
    },
    []
  );

  return (
    <>
      <IntegrationPanelContainer
        isConnected={facebookPages.length > 0}
        connectedContent={
          <div
            className="ColumnNormal"
            style={{ gap: 14, width: "100%", paddingBlock: 14 }}
          >
            <div
              className="AlignedRowSpaced"
              style={{ paddingInline: 14, alignItems: "flex-start", gap: 4 }}
            >
              <div className="ColumnNormal" style={{ gap: 4 }}>
                <span className="sectionTitle">Manage Connections</span>
                <span className="smallBodySubtext">
                  View your connected accounts and manage their settings
                </span>
              </div>
              {!atCap ? (
                <RectangleButton
                  buttonLabel="Connect New"
                  onPress={connectOnPress}
                  altColor={Colors.BLACK}
                  altTextColor={Colors.WHITE}
                  altPaddingHorz={14}
                  altPaddingVert={12}
                  containerStyles={{
                    width: "fit-content",
                    whiteSpace: "nowrap",
                  }}
                />
              ) : null}
            </div>
            <HorizontalDivider altMargin={0} />
            <div style={{ maxHeight: 160, overflowY: "auto" }}>
              {facebookPages.map((facebookPage, index) =>
                renderAccountRow(
                  facebookPage,
                  index === facebookPages.length - 1
                )
              )}
            </div>
          </div>
        }
        title="Connect Facebook"
        description="Start communicating with your customers via Facebook Messenger."
        actionLabel={hasSub ? "Connect" : "Upgrade"}
        onActionPress={connectOnPress}
        icon={
          <SocialPlatformIconItem
            platform={DMAutomationPlatform.FACEBOOK}
            size={90}
            noBorder
          />
        }
      />
      <ConnectFacebookModal
        showModal={showConnectModal}
        setShowModal={setShowConnectModal}
      />
      {facebookPage ? (
        <ManageFacebookAccountModal
          facebookPage={facebookPage}
          showModal={!!facebookPage}
          setShowModal={(show) => {
            if (!show) setManageAccountId("");
          }}
        />
      ) : null}
    </>
  );
};
